import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Component, Type } from '@angular/core';
import { async } from '@angular/core/testing';
import { AbstractControl, AsyncValidatorFn, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { Response } from '@angular/http';
import { Logger } from 'app/shared/logger';
import { WebPageModel } from 'app/views/presentations/widgets/webpage/webpage.model';
import { Widget } from 'app/views/presentations/widgets/widget';
import { WidgetComponent } from 'app/views/presentations/widgets/widget.component';
import 'rxjs/add/observable/fromPromise';
import { Observable } from 'rxjs/Observable';
import { setTimeout } from 'timers';
import { AppConfig } from '../../../../app.config';
import { LoggerFactory } from '../../../../shared/loggerFactory';

export class WebpageWidget extends Widget {
    public type: string = 'Webpage';
    public icon: string = 'fa-globe';
    public nameResourceKey: string = 'modules.editors.webpage.name';
    public componentType: Type<WidgetComponent> = WebpageWidgetComponent;
}

@Component({
    selector: 'psm-webpage-widget',
    templateUrl: 'webpage.widget.html',
    styleUrls: ['webpage.widget.scss']
})
export class WebpageWidgetComponent extends WidgetComponent<WebPageModel> {
    public url: string = '';
    private logger: Logger;

    constructor(loggerFactory: LoggerFactory, private httpClient: HttpClient, private appConfig: AppConfig) {
        super();
        this.logger = loggerFactory.create('WebpageWidgetComponent');
    }

    private isUrl(str: string) {
        const pattern = new RegExp('^(https?:\\/\\/)?' + // protocol
            '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.?)+[a-z]{2,}|' + // domain name
            '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
            '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
            '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
            '(\\#[-a-z\\d_]*)?$', 'i'); // fragment locator
        return pattern.test(str);
    }

    public showValidation = false;

    public form: FormGroup;

    private checkUrlTimeout;

    private static readonly cachedErrors = new Map<string, any>();

    private validateUrl(control: AbstractControl) {
        if (!control.value || !this.isUrl(control.value)) {
            return Observable.of({ 'modules.editors.webpage.no-animation-error': true });
        }

        let url: string = control.value;
        if (WebpageWidgetComponent.cachedErrors.has(url)) {
            let errors = WebpageWidgetComponent.cachedErrors.get(url);
            return Observable.of(errors);
        } else {
            return Observable.timer(2000).switchMap(async () => {
                if (!url.startsWith('http://') && !url.startsWith('https://')) {
                    url = 'http://' + url;
                    control.setValue(url);
                }
                const escapedUrl = encodeURIComponent(url);
                let result = await this.httpClient.get<any>(this.appConfig.getApiUrl(`/presentation/getUrlHeader?url=${escapedUrl}`)).toPromise();
                let errors = this.validateResponse(result);
                WebpageWidgetComponent.cachedErrors.set(url, errors);
                return errors;
            });
        }
    }

    private validateResponse(result: any) {
        try {
            if (result.error) {
                let err = result.error;
                if (err.code === 'ENOTFOUND') {
                    return { 'modules.editors.webpage.not-found': true };
                }
            }

            if (result.response) {
                let statusCode = result.response.statusCode;
                if (statusCode < 400) {
                    if (result.response && Object.keys(result.response.headers).map(x => x.toLowerCase()).filter(x => x === 'X-Frame-Options'.toLowerCase()).length > 0) {
                        return { 'modules.editors.webpage.website-prevent-embedding': true };
                    }
                    return;
                } else if (statusCode === 401) {
                    return { 'modules.editors.webpage.unauthorized': true };
                } else if (statusCode === 403) {
                    return { 'modules.editors.webpage.forbidden': true };
                } else if (statusCode === 404) {
                    return { 'modules.editors.webpage.not-found': true };
                } else if (statusCode === 405) {
                    return { 'modules.editors.webpage.website-prevent-embedding': true };
                } else if (statusCode >= 500 && statusCode < 600) {
                    // This is a server side error, i guess we should allow the page to be added, it could be
                    // resolved in the future, could be temporary unavailable, etc
                } else {
                    this.logger.error('An error unknown error occured while validating website.', result);
                    return { 'modules.editors.webpage.unknown': true };
                }
            }
        } catch (err) {
            this.logger.error('An error unknown error occured while validating website.', err);
            return { 'modules.editors.webpage.unknown': true };
        }

    }

    public async initialize() {
        this.url = this.content ? this.content.url : undefined;
        this.form = new FormGroup({
            url: new FormControl(this.url, [], control => this.validateUrl(control))
        });
        this.form.controls.url.valueChanges.subscribe(value => this.onChange(value));
        if (this.url) {
            // Mark the value as touched if we already have one
            setTimeout(() => this.form.controls.url.markAsTouched(), 0);
        }
    }

    public onChange(ev) {
        let previousContentUrl = this.content ? this.content.url : undefined;
        if (previousContentUrl !== this.url) {
            this.content = new WebPageModel(this.url);
            this.form.controls.url.markAsTouched();
        }
    }

    public async getFormControls(): Promise<AbstractControl[]> {
        return [this.form];
    }
}



// WEBPACK FOOTER //
// ./src/app/views/presentations/widgets/webpage/webpage.widget.ts